/**
 * The trade binder is the unit of trading.
 *
 * A trade request asks to meet, not to trade. What the other person looks at before
 * saying yes is this one binder, and what you carry to the table is this one binder.
 * So "tradable" does not mean "in some binder", it means "in THE binder".
 *
 * One per person until it holds 300 cards, which is what a real binder holds. The next
 * is created automatically. Nobody picks one, nobody is asked which.
 *
 * The rule is enforced in the database by `cc_trade_binder_guard`, a BEFORE trigger on
 * `cards` that relocates rather than refuses. That matters: a refusal would surface as
 * a failed save on a screen that did nothing wrong, usually while someone watches a
 * spinner after scanning forty cards. So the client never has to ask which binder, and
 * a screen that forgets the rule still cannot break it.
 */
import { supabase } from '../lib/supabase';

export const TRADE_BINDER_NAME = 'Trade Binder';
export const TRADE_BINDER_LIMIT = 300;

/** The (i) copy. Short on purpose: it is a tooltip, not a policy page. */
export const TRADE_BINDER_EXPLAINER =
  'Everything you mark tradable lives in your Trade Binder, because that is the binder '
  + 'you actually bring to a trade. It keeps "I have it" and "I brought it" the same '
  + 'promise, and it is what another trader looks at before agreeing to meet.';

/** Said at the moment of marking, because the card physically leaves where it was. */
export const TRADE_BINDER_MOVE_NOTE =
  'Marking this tradable moves it to your Trade Binder.';

/** Plural, for a multi-select. */
export function tradeBinderMoveNote(count) {
  return count === 1
    ? TRADE_BINDER_MOVE_NOTE
    : `Marking these tradable moves ${count} cards to your Trade Binder.`;
}

/** Said next to the PC / Tradable choice anywhere a card is first saved. */
export const TRADE_STATUS_CHANGEABLE_NOTE = 'You can change this later.';

/** Shown when someone tries to pull a tradable card out of the trade binder. */
export const TRADE_NEEDS_BINDER_WARNING =
  'This card is marked tradable, so it lives in your Trade Binder. Mark it PC first to '
  + 'move it somewhere else.';

/**
 * The trade binder with room in it, created if there is none and rolled over at 300.
 * Idempotent in the database, so callers never look first.
 * @returns {Promise<string|null>} the binder id, or null if the call failed.
 */
export async function ensureTradeBinder(userId) {
  if (!userId) return null;
  const { data, error } = await supabase.rpc('cc_ensure_trade_binder', { p_user_id: userId });
  if (error) {
    console.error('[tradeBinder] ensureTradeBinder failed:', error);
    return null;
  }
  return data || null;
}

/**
 * Soft-delete a binder without stranding anything tradable inside it. Tradable cards
 * land in a trade binder - a fresh one if this was the trade binder - personal ones go
 * loose, and the binder row is kept so a dispute can still name what it held.
 */
export async function deleteBinder(binderId) {
  const { data, error } = await supabase.rpc('cc_delete_binder', { p_binder_id: binderId });
  if (error) throw error;
  return data || { moved_to_trade_binder: 0, released: 0 };
}

/**
 * Names the app keeps for itself.
 *
 * The trade binder is a system object, like an inbox. Another trader looks at it before
 * agreeing to meet, so "Trade Binder" has to mean the same thing on every profile - and
 * an ordinary binder wearing the name makes the label a lie. Two accounts already had
 * an empty binder called "Trade Binder 1" sitting beside their real one.
 *
 * Reserved exactly, not fuzzily. "Trade VNR", "Trades" and "Trade Day Pulls" are all
 * still fine: an exact collision is the real harm, and guessing at intent would refuse
 * names nobody meant as an impersonation. Enforced again in the database by
 * `cc_trade_binder_name_guard`, which is what actually holds the line.
 */
export function isReservedBinderName(name) {
  return /^trade\s*binder\s*\d*$/i.test(String(name ?? '').trim());
}

export const RESERVED_BINDER_NAME_ERROR = 'That name belongs to your Trade Binder.';

/** True for the binder tradable cards live in. */
export function isTradeBinder(binder) {
  return !!binder?.is_trade_binder;
}
